import { useTranslation } from 'react-i18next';

import { AppText, Button, IconBox } from '@zippeditoolsjs/ui-web';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import AddSecondFactorOptIn from './AddSecondFactorOptIn';
import OtpCodeInput from '../../utils/OtpCodeInput';
import { getPhoneHint } from '../policy/totpChallenge';

// TOTP login challenge form. Unlike the SMS flow there is nothing to send or
// resend: the code already lives in the user's authenticator app.
export default function TOTPCode(props) {
  const { t } = useTranslation();
  const {
    resolver,
    sendingOPT,
    setCaptchaCode,
    setLoginErrorKey,
    verifyTotpCode,
    onTryAnotherWay,
    offerFactor,
    optInChecked,
    onToggleOptIn,
  } = props;
  // Clearing the code upstream (e.g. after a wrong code) empties the cells:
  // OtpCodeInput remounts itself whenever this value changes.
  const code = props.code ? props.code : '';
  const phoneHint = getPhoneHint(resolver);
  const showOptIn = Boolean(offerFactor) && Boolean(onToggleOptIn);

  const handleChangeCode = newValue => {
    // No auto-submit at six digits: verification needs an explicit press, so a
    // mistyped digit cannot burn the code on its own.
    setCaptchaCode(newValue);
    setLoginErrorKey(null);
  };

  return (
    <div className="flex flex-col">
      <IconBox className="mb-4">
        <IconComponent iconName="shield-checkmark" style={{ fontSize: 22 }} />
      </IconBox>

      <AppText variant="titleXLarge" as="h3" className="mb-2">
        {t('heimdal_app.TOTP.totp_code_title')}
      </AppText>
      <AppText variant="bodyMedium" as="p" className="mb-6 text-secondary">
        {t('heimdal_app.TOTP.enter_code')}
      </AppText>

      <div className="mb-3.5">
        <OtpCodeInput
          value={code}
          label={t('heimdal_app.TOTP.totp_code_title')}
          disabled={sendingOPT}
          onChange={handleChangeCode}
        />
      </div>
      <p className="m-0 mb-3.5 font-sans text-xs text-muted">
        {t('heimdal_app.TOTP.code_renews')}
      </p>

      {showOptIn ? (
        <AddSecondFactorOptIn
          factor={offerFactor}
          checked={Boolean(optInChecked)}
          onToggle={onToggleOptIn}
          disabled={sendingOPT}
        />
      ) : null}

      <Button
        fullWidth
        loading={sendingOPT}
        disabled={code.length < 6 || sendingOPT}
        onClick={() => verifyTotpCode(code)}>
        {showOptIn && optInChecked
          ? t('heimdal_app.TOTP.verify_and_add')
          : t('heimdal_app.TOTP.verify')}
      </Button>

      {phoneHint && onTryAnotherWay ? (
        <button
          type="button"
          onClick={onTryAnotherWay}
          // Stays locked while a verification is in flight, and through the
          // terminal success (sendingOPT is held until the redirect lands).
          disabled={sendingOPT}
          className="mt-3.5 flex cursor-pointer items-center justify-center gap-1.5 border-0 bg-transparent p-1 font-sans text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted transition-colors hover:text-accent disabled:cursor-not-allowed disabled:opacity-60">
          {t('heimdal_app.OTP.use_another_method')}
        </button>
      ) : null}
    </div>
  );
}
